import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import Avatar, { genConfig } from 'funifier-nice-avatar';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';
import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { useModal } from 'context/ModalContext';
import { uploadPicture, updateUserProfile } from 'services/profile.service';
import { useAuth } from 'context/AuthContext';
import { avisarFotoGravada } from 'pages/Selos/useColecaoDeSelos';
import { toast } from 'react-toastify';
import { BiTrash } from 'react-icons/bi';
import { FiCheck } from 'react-icons/fi';
import { AvatarConfig, GetAvatarDefaultOptions } from './config';

import noImage from 'assets/img/no-image.png';
import { RiImageEditFill } from 'react-icons/ri';

const config = genConfig();

const avatarDefaultOptions = GetAvatarDefaultOptions() as any;

let imagePreviewTimeout: any = null;

/** Partes do avatar, na ordem da tela: cada botão avança para a próxima opção daquela parte. */
const AVATAR_PARTS: { type: keyof AvatarConfig; label: string }[] = [
  { type: 'faceColor', label: 'Pele' },
  { type: 'eyeStyle', label: 'Olhos' },
  { type: 'glassesStyle', label: 'Óculos' },
  { type: 'earSize', label: 'Orelhas' },
  { type: 'mouthStyle', label: 'Boca' },
  { type: 'noseStyle', label: 'Nariz' },
  { type: 'hairStyle', label: 'Cabelo' },
  { type: 'hairColor', label: 'Cor do Cabelo' },
  { type: 'hatStyle', label: 'Acessório' },
  { type: 'hatColor', label: 'Cor do Acc.' },
  { type: 'shirtStyle', label: 'Camisa' },
  { type: 'shirtColor', label: 'Cor' },
  { type: 'bgColor', label: 'Fundo' },
];

export function AvatarModal(props: any) {
  // const modalID = "achievement";

  const { user, refreshUser } = useAuth();

  const modalID = 'avatar';

  const [loading, setLoading] = useState(false);

  const [modalIsOpen, setIsOpen] = useState(false);
  const [avatarConfig, setAvatarConfig] = useState<AvatarConfig>(config);

  const { openModal, modalData, closeModal } = useModal();

  const [avatarType, setAvatarType] = useState(0);

  const [image, setImage] = useState('');
  const editor = useRef<any>(null);

  /** Texto da região invisível de status: troca de parte do avatar e imagem carregada. */
  const [announcement, setAnnouncement] = useState('');
  const deleteImageRef = useRef<HTMLButtonElement>(null);

  const hasSavedImage = !!(user && user.image && user.image.medium);

  function handleCloseModal() {
    setImage('');
    setLoading(false);
    setAvatarType(0);
    setAnnouncement('');
    closeModal();
  }

  /** Esc, X e Cancelar: enquanto salva, não fecham, para o salvamento não terminar depois do "cancelar". */
  function handleRequestClose() {
    if (loading) return;
    handleCloseModal();
  }

  function handleSave() {
    if (loading) return;
    handleUpload();
  }

  /** Se o envio ou a gravação falhar, avisa e libera o modal: Esc, X e Cancelar ficam bloqueados enquanto `loading`. */
  async function handleUpload() {
    if (!user) return;

    let profileImage: any = {};

    setLoading(true);

    try {
      if (image) {
        const dataUrl = editor.current.getImageScaledToCanvas().toDataURL();

        if (dataUrl) {
          const uploadResult = await uploadPicture(dataUrl);

          if (uploadResult) {
            const url = uploadResult.url;
            profileImage = {
              image: {
                small: { url },
                medium: { url },
                original: { url },
              },
            };
          }
        }
      }

      profileImage['extra'] = {
        ...user.extra,
        avatar_type: avatarType,
        avatar: avatarConfig,
      };

      await updateUserProfile({ _id: user._id, ...profileImage });
      avisarFotoGravada();
      refreshUser();
      handleCloseModal();
      toast(`👍 Seu perfil foi atualizado com sucesso!`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch {
      toast.error('Não foi possível salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleImageChange() {
    if (imagePreviewTimeout) {
      clearTimeout(imagePreviewTimeout);
    }

    imagePreviewTimeout = setTimeout(() => {
      getImageUrl();
    }, 1000);
  }

  function handleClearImage() {
    setAnnouncement('');
    setImage('');
  }

  function handleDrop(dropped: any) {
    setImage(dropped[0]);
    setAnnouncement('Imagem carregada.');
  }

  const getImageUrl = async () => {
    if (!editor.current) return;

    const dataUrl = editor.current.getImageScaledToCanvas().toDataURL();
    const result = await fetch(dataUrl);
    const blob = await result.blob();

    const profileImage = document.getElementById('profile-image');
    if (profileImage) {
      profileImage.style.backgroundImage = `url(${window.URL.createObjectURL(blob)})`;
    }

    // return window.URL.createObjectURL(blob)
  };

  function getImagePlaceHolder() {
    if (!image && hasSavedImage) {
      return { backgroundImage: `url(${user?.image.medium.url})` };
    } else
      return {
        backgroundImage: `url(${noImage})`,
        backgroundSize: 'cover',
      };
  }

  // function switchAvatarPart(part: string) {
  //   const newAvatarConfig = { ...avatarConfig };
  //   console.log(newAvatarConfig);
  //   setAvatarConfig(newAvatarConfig);
  // }

  function switchAvatarPart(type: string, currentOpt: any, label: string) {
    const opts: any = avatarDefaultOptions[type];
    const currentIdx = opts.findIndex((item: any) => item === currentOpt);
    const newIdx = (currentIdx + 1) % opts.length;
    const newAvatarConfig = { ...avatarConfig, [type]: opts[newIdx] };

    if (type === 'hairStyle') {
      newAvatarConfig['hatStyle'] = 'none';
    }

    setAvatarConfig(newAvatarConfig);
    setAnnouncement(`${label}: opção ${newIdx + 1} de ${opts.length}`);
  }

  // Com o arquivo escolhido, a área de envio deixa de ser focável: o foco vai para "Excluir imagem".
  // Só depende de haver arquivo, então abrir a aba com foto salva não move o foco.
  useEffect(() => {
    if (image) deleteImageRef.current?.focus();
  }, [image]);

  useEffect(() => {
    if (!user) return;

    setIsOpen(openModal === modalID);

    if (modalData && modalData.config) {
      setAvatarConfig(modalData.config);

      if (user.extra.avatar_type) {
        setAvatarType(user.extra.avatar_type);
      }
    }
  }, [user, openModal, modalData]);

  return (
    <ModalFrame
      isOpen={modalIsOpen}
      title="Editar avatar"
      size="md"
      onClose={handleRequestClose}
      footer={
        <>
          <p role="status" className="m-0 text-sm font-bold text-content">
            {loading ? 'Salvando alterações…' : ''}
          </p>

          <div className="ml-auto flex gap-2.5">
            <Button variant="secundario" size="sm" aria-disabled={loading} onClick={handleRequestClose}>
              Cancelar
            </Button>
            <Button variant="principal" size="sm" disabled={loading} onClick={handleSave}>
              Salvar
            </Button>
          </div>
        </>
      }
    >
      <div className="flex flex-col items-center gap-5 px-5 py-6 text-content lg:px-8">
        <span className="sr-only" role="status">
          {announcement}
        </span>

        <div role="group" aria-label="Tipo de foto de perfil" className="flex gap-2.5">
          <AvatarTypeOption label="Avatar" active={avatarType === 0} onSelect={() => setAvatarType(0)} />
          <AvatarTypeOption label="Imagem" active={avatarType === 1} onSelect={() => setAvatarType(1)} />
        </div>

        {avatarType === 0 && (
          <div className="flex w-full flex-col items-center gap-4">
            <div role="img" aria-label="Pré-visualização do avatar">
              <Avatar className="h-36 w-36" {...avatarConfig} />
            </div>

            <div
              role="group"
              aria-label="Partes do avatar"
              className="grid w-full max-w-[480px] grid-cols-2 gap-2 sm:grid-cols-3"
            >
              {AVATAR_PARTS.map(({ type, label }) => (
                <Button
                  key={type}
                  variant="secundario"
                  size="sm"
                  onClick={() => switchAvatarPart(type, avatarConfig[type], label)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {avatarType === 1 && (
          <div className="relative h-64 w-64 rounded-full bg-surface-200" style={getImagePlaceHolder()}>
            <Dropzone
              onDrop={handleDrop}
              multiple={false}
              maxFiles={1}
              noClick={!!image}
              noKeyboard={!!image}
              accept={{
                'image/png': ['.png', '.jpeg', '.jpg', '.gif'],
              }}
            >
              {({ getRootProps, getInputProps, rootRef }) => (
                <>
                  <div
                    {...getRootProps(
                      image ? {} : { role: 'button', 'aria-label': hasSavedImage ? 'Enviar nova imagem' : 'Enviar imagem' },
                    )}
                    className={`rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark ${
                      !image ? '!cursor-pointer' : ''
                    }`}
                  >
                    <AvatarEditor
                      ref={e => (editor.current = e)}
                      onImageChange={handleImageChange}
                      width={256}
                      height={256}
                      image={image}
                      borderRadius={256}
                      border={0}
                      className={`overflow-hidden rounded-full ${!image ? '!cursor-pointer' : ''}`}
                    />
                    <input {...getInputProps()} />
                  </div>

                  {!!image && (
                    <button
                      ref={deleteImageRef}
                      type="button"
                      onClick={() => {
                        // O botão some com o foco nele: a área de envio, que volta a ser focável
                        // depois de renderizar, o recebe.
                        flushSync(handleClearImage);
                        rootRef.current?.focus();
                      }}
                      aria-label="Excluir imagem"
                      className="absolute bottom-[5px] right-[5px] flex h-10 w-10 items-center justify-center rounded-full bg-brand-white text-brand-blue shadow-jj-02 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark"
                    >
                      <BiTrash size={24} aria-hidden="true" />
                    </button>
                  )}
                </>
              )}
            </Dropzone>

            {!image && hasSavedImage && (
              // Só indica que a foto pode ser trocada: quem age é a área de envio, que já tem o nome.
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[5px] right-[5px] flex h-10 w-10 items-center justify-center rounded-full bg-brand-white text-brand-blue shadow-jj-02"
              >
                <RiImageEditFill size={22} />
              </span>
            )}
          </div>
        )}
      </div>
    </ModalFrame>
  );
}

interface AvatarTypeOptionProps {
  label: string;
  active: boolean;
  onSelect: () => void;
}

/** Opção AVATAR/IMAGEM. A ativa muda de cor e ganha o ícone de marcado, para não depender só da cor. */
function AvatarTypeOption({ label, active, onSelect }: AvatarTypeOptionProps) {
  return (
    <Button variant={active ? 'solido' : 'secundario'} size="sm" aria-pressed={active} onClick={onSelect} className="gap-1.5">
      {active && <FiCheck size={14} strokeWidth={3} aria-hidden="true" />}
      {label}
    </Button>
  );
}
